<script>
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import VerticalLayout from '../VerticalLayout/Index.svelte';
	import HorizontalLayout from '../HorizontalLayout/Index.svelte';
	import { addMessages, init, getLocaleFromNavigator } from 'svelte-i18n';
	import en from '../lang/en.json';
	import { authCheck } from '../services/auth.js';
	import { apiEndpoint } from '../services/api.js';
	import { installDialogSearch } from '../common/dialogSearch.js';

	addMessages('en', en);

	init({
		fallbackLocale: 'en',
		initialLocale: getLocaleFromNavigator()
	});

	let layoutType = 'vertical';
	let googleAnalyticsConfig = null;
	let googleAnalyticsLastPath = '';

	function googleAnalyticsMeasurementId() {
		return String(googleAnalyticsConfig?.measurement_id || '')
			.trim()
			.toUpperCase();
	}

	function canUseGoogleAnalytics() {
		return (
			googleAnalyticsConfig?.enabled === true &&
			/^G-[A-Z0-9]+$/.test(googleAnalyticsMeasurementId())
		);
	}

	function ensureGoogleAnalyticsScript() {
		const measurementId = googleAnalyticsMeasurementId();
		if (
			!canUseGoogleAnalytics() ||
			typeof window === 'undefined' ||
			typeof document === 'undefined'
		)
			return false;

		window.dataLayer = window.dataLayer || [];
		window.gtag =
			window.gtag ||
			function () {
				window.dataLayer.push(arguments);
			};

		const scriptId = 'dcine-google-analytics-gtag';
		if (!document.getElementById(scriptId)) {
			const script = document.createElement('script');
			script.id = scriptId;
			script.async = true;
			script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
			document.head.appendChild(script);
			window.gtag('js', new Date());
		}

		return true;
	}

	function trackGoogleAnalyticsPage(url) {
		if (!url || !ensureGoogleAnalyticsScript()) return;

		const measurementId = googleAnalyticsMeasurementId();
		// Confirmation keys and offer recipient details must not enter analytics.
		const sensitive = /\/(auth|booking_offer_response)(\/|$)/.test(url.pathname);
		const pagePath = `${url.pathname}${sensitive ? '' : url.search}`;
		if (googleAnalyticsLastPath === pagePath) return;

		googleAnalyticsLastPath = pagePath;
		window.gtag('config', measurementId, {
			page_path: pagePath,
			...(sensitive ? { page_location: `${url.origin}${url.pathname}` } : {}),
			page_title: document.title
		});
	}

	async function loadGoogleAnalyticsConfig() {
		try {
			const response = await fetch(`${apiEndpoint}/dcns/GoogleAnalyticsConfig/`, {
				credentials: 'include',
				headers: {
					Accept: 'application/json'
				}
			});
			if (!response.ok) return;
			googleAnalyticsConfig = await response.json();
			trackGoogleAnalyticsPage($page.url);
		} catch (error) {
			console.debug('Google Analytics config load failed', error);
		}
	}

	onMount(() => {
		const removeDialogSearch = installDialogSearch();
		loadGoogleAnalyticsConfig();
		// Check login when you first hit the website.
		void authCheck(true);
		//
		const intervalId = setInterval(() => {
			console.debug('PERIODIC authCheck()');
			authCheck();
		}, 90000);

		return () => {
			clearInterval(intervalId);
			removeDialogSearch();
		};
	});

	let component = layoutType == 'vertical' ? VerticalLayout : HorizontalLayout;

	const publicRoutes = [
		'/s1/auth/login',
		'/s1/auth/register',
		'/s1/auth/recoverpw',
		'/s1/auth/recoverpw_p2',
		'/s1/auth/lock-screen',
		'/s1/auth/confirm-mail',
		'/s1/auth/email-verification',
		'/s1/auth/two-step-verification',
		'/s1/slate/booking_offer_response',
		'/s1/pages/maintenance',
		'/s1/pages/comingsoon',
		'/s1/pages/404',
		'/s1/pages/500'
	];
	$: isPublic = publicRoutes.includes($page.url.pathname.replace(/\/$/, ''));
	$: if (googleAnalyticsConfig && $page.url) {
		trackGoogleAnalyticsPage($page.url);
	}
</script>

<svelte:head>
	<!--	<script src="//cdn.jsdelivr.net/npm/apexcharts"></script>-->
	<!-- jsvectormap -->
	<!--	<script src="https://cdn.jsdelivr.net/npm/jsvectormap"></script>-->
	<!--	<script src="https://cdn.jsdelivr.net/npm/jsvectormap/dist/maps/world.js"></script>-->
</svelte:head>

{#if isPublic}
	<slot />
{:else}
	<svelte:component this={component}>
		<slot />
	</svelte:component>
{/if}

<style lang="scss">
	@import './../assets/scss/bootstrap.scss';
	@import './../assets/scss/app.scss';
	@import './../assets/scss/icons.scss';
</style>
